import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Form } from 'redux-form';
import Field from './Field';
import CustomQuestions from '../components/CustomQuestions';
import { Button, Col, Row, HeadingFive } from '../../legacy/hui/src/styled';
import Loader from './Loader';
import SuburbSelect from './SuburbSelect';
import styled from 'styled-components';
import { darken } from 'polished/lib/index';
import { Theme } from './Bunnings/Theme';
import logo from '../images/hipages-logo.svg';
import logoSmall from '../images/hipages-logo-small.svg';
import CustomAddress from './CustomAddress';
import { phone, email, address } from '../utils/validation';
import Categories from '../constants/Categories';
import getCategoryId from '../selectors/categoryId';
import capFirstChar from '../utils/capFirstChar';
import UploadAttachment from './UploadAttachment';
import { attachmentUrl } from '../api/attachment';

export const CATEGORY_FIELD = 'search_str';

export const StyledHr = styled.hr`
  margin: 30px 0;
  color: #ececec;
  border-width: 0.5px;
  border-style: solid;
  border-color: #ececec;
`;

const ButtonSubmit = Button.extend`
  color: #fff;
  background-color: ${Theme.primary};
  &:active,
  &:hover,
  &:focus {
    background-color: ${darken(0.1, Theme.primary)};
  }
`;

export const TitleBarWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  height: 42px;
  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    height: 50px;
  }
  margin-right: 20px;
`;

export const Logo = styled.div`
  width: 65px;
  height: 23px;
  background: url(${logoSmall}) no-repeat;
  text-indent: -99999px;
  display: inline-block;
  background-size: contain;
  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    background: url(${logo}) no-repeat;
    width: 104px;
    height: 38px;
    background-size: contain;
  }
`;

export const PowerBy = styled.div`
  color: #a1a1a3;
  font-size: 10px;
  line-height: 14px;
  padding-right: 5px;
  padding-top: 2px;
  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    font-size: 14px;
    line-height: 20px;
    padding-right: 12px;
  }
`;

export function GetQuotesForm({
  submitting,
  questions,
  handleSubmit,
  buttonText,
  change,
  categories
}) {
  const user = {}; // todo add user obj
  const cid = getCategoryId(categories);
  const categoryName = Categories[cid].name;
  const categoryPureName = Categories[cid].pureName;
  const installationTime = Categories[cid].installationTime;
  const capCategory = capFirstChar(categoryName);
  return (
    <Form onSubmit={handleSubmit} autoComplete="new-password">
      <HeadingFive>
        Tell us about your <span style={{ fontWeight: 600 }}>{capCategory}</span> job
      </HeadingFive>
      <Col xs={12} style={{ marginRight: 11 }} mt={2}>
        <SuburbSelect
          label={`Which suburb is the job in?`}
          name="w"
          placeholder="postcode and suburb e.g. 2000 - Sydney"
          autoComplete="new-password"
        />
        <div style={{ marginTop: 25 }}>
          <CustomAddress
            setSuburb={change}
            relatedFieldName="w"
            label="Your address"
            name="address_full"
            fieldInfoLabel="Address not shared with tradies"
            fieldInfoValue="Your address is private unless you choose to share it <br /> with the tradie assigned for your job."
            validate={[address]}
          />
        </div>
      </Col>

      <Col xs={12} style={{ marginRight: 11 }}>
        <CustomQuestions
          questions={questions}
          codeCat={categoryPureName}
          installationTime={installationTime}
        />
      </Col>

      <Col xs={12} style={{ marginRight: 11 }}>
        <Field
          name="attachments"
          component={UploadAttachment}
          uploadUrl={attachmentUrl}
          headerText="Please share with the tradie a photo of where product will be installed and a photo of the purchased product"
          tipText="A maximum of 5 photos can be uploaded"
          maxCount={5}
          maxSize={5}
        />
      </Col>

      <StyledHr />
      <HeadingFive>My Details</HeadingFive>

      <Col xs={12} style={{ marginTop: 20, marginRight: 11 }}>
        {user.name ? (
          <div>
            <span style={{ fontWeight: 600 }}>Your Name</span> <p>{user.name}</p>
          </div>
        ) : (
          <Field label="Your name" name="full_name" />
        )}
        {user.email ? (
          <div style={{ marginBottom: 25 }}>
            <span style={{ fontWeight: 600 }}>Your Email</span> <p>{user.email}</p>
          </div>
        ) : (
          <Field
            label="Your email"
            name="email"
            fieldInfoLabel="Why do we need your email address?"
            fieldInfoValue="We need your email address that we can send you updates regarding your job."
            validate={[email]}
          />
        )}
        <Field
          label="Best number to contact you on"
          name="phone"
          fieldInfoLabel="Why do we need your phone number?"
          fieldInfoValue="We need your phone number so that the assigned tradie <br /> for your job can arrange the installation time."
          validate={[phone]}
        />
      </Col>
      <Col xs={12} style={{ marginTop: 20, marginRight: 11 }}>
        Installing new products in your home can occasionally cause unavoidable damage to existing
        fixtures. The tradie we connect you with is responsible for providing a professional
        service, however some damage may be outside of their control and something they are not
        liable for. For example, removing an old toilet may crack some surrounding tiles or
        replacing a ceiling fan may expose problems with your ceiling.
      </Col>
      <Col xs={12} style={{ marginTop: 20, marginBottom: 20, marginRight: 11 }}>
        We recommend discussing any potential problems with your tradie prior to the job commencing.
        If you decide not to proceed with your installation in light of this information, you can
        return to your Bunnings store for a refund.
      </Col>
      <Row>
        <Col xs={12} sm={6} mt={{ xs: 0.1, md: 0.3 }}>
          <ButtonSubmit primary disabled={submitting}>
            {submitting ? <Loader /> : buttonText}
          </ButtonSubmit>
        </Col>
        <Col xs={12} sm={6} pr={1}>
          <TitleBarWrapper style={{ justifyContent: 'flex-end' }}>
            <PowerBy>Powered by</PowerBy>
            <Logo />
          </TitleBarWrapper>
        </Col>
      </Row>
    </Form>
  );
}

GetQuotesForm.propTypes = {
  submitting: PropTypes.bool,
  questions: PropTypes.array,
  handleSubmit: PropTypes.func,
  initialLocation: PropTypes.string,
  buttonText: PropTypes.string,
  change: PropTypes.func,
  categories: PropTypes.array
};

export function scrollToFirstError(errors) {
  const [firstField] = Object.keys(errors);

  if (typeof document === 'undefined') {
    return;
  }

  if (firstField === 'search_str') {
    const node = document.querySelector('.Select-control');
    node.scrollIntoView({
      behavior: 'smooth'
    });
    return;
  }
  const node = document.querySelector(`[name="${firstField}"]`);
  if (!!node === false) {
    throw `could not find field ${firstField}`;
  }
  node.parentElement.scrollIntoView({
    behavior: 'smooth'
  });
}

const ConnectedForm = reduxForm({
  form: 'GQ',
  onSubmitFail: scrollToFirstError
})(GetQuotesForm);

const initialValues = (state, props) => {
  const { initialLocation } = props;
  return {
    w: initialLocation
  };
};

export default connect((state, props) => ({
  initialValues: initialValues(state, props)
}))(ConnectedForm);
